import { useEffect, useRef, useState } from 'react'
import { getRole, getRolesMeta, createRole, updateRole, type Capability } from '../api'
import { useTabs, useDirtyGuard, stableJson, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { textOf, MODULE_LABELS, CAP_LABELS } from '../../shared/texts'
import InfoTip from '../components/InfoTip'
import { checkBeforeSave } from '../forms/checkForm'
import { PAGES_ROLE_EDITOR_TEXTS as T } from '../../shared/texts'

type CapMap = Record<string, Capability[]>

const empty = { key: '', label: '', capabilities: {} as CapMap }

// write/delete imply read (can't act on what you can't see).
function toggle(caps: CapMap, module: string, cap: Capability): CapMap {
    const cur = new Set(caps[module] ?? [])
    if (cur.has(cap)) {
        cur.delete(cap)
        if (cap === 'read') { cur.delete('write'); cur.delete('delete') }
    } else {
        cur.add(cap)
        if (cap === 'write' || cap === 'delete') cur.add('read')
    }
    const ordered = (['read', 'write', 'delete'] as Capability[]).filter((c) => cur.has(c))
    // Nothing left → drop the module instead of leaving an empty list behind.
    // Both mean the same to the server (sanitizeCapabilities prunes empty
    // modules and replaces the whole object), but as JSON text they differ — and
    // that difference is what kept "Übernehmen" enabled after taking a change
    // back on a module that started out with no rights at all.
    if (!ordered.length) {
        const { [module]: _dropped, ...rest } = caps
        return rest
    }
    return { ...caps, [module]: ordered }
}

// Compare capability maps by MEANING, not by JSON text: drop empty modules and put
// every list in the fixed read/write/delete order. The baseline comes straight from
// the server and is not guaranteed to be in that shape (an older role may carry
// e.g. ['write','read']), so normalising only the edited side would still leave the
// button stuck. Key order is handled by stableJson below.
function normalizeCaps(caps: CapMap): CapMap {
    const out: CapMap = {}
    for (const module of Object.keys(caps)) {
        const list = (['read', 'write', 'delete'] as Capability[]).filter((c) => caps[module]?.includes(c))
        if (list.length) out[module] = list
    }
    return out
}
const sameRole = (a: typeof empty, b: typeof empty) =>
    stableJson({ ...a, capabilities: normalizeCaps(a.capabilities) })
    === stableJson({ ...b, capabilities: normalizeCaps(b.capabilities) })

export default function RoleEditor({ tabId, id }: { tabId?: string, id?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange, closeTab } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const readOnly = !can('roles', 'write') // role lacks roles:write → view only
    const [modules, setModules] = useState<string[]>([])
    const [parents, setParents] = useState<Record<string, string>>({})
    const [caps, setCaps] = useState<Capability[]>(['read', 'write', 'delete'])
    const [moduleCaps, setModuleCaps] = useState<Record<string, Capability[]>>({})
    const [form, setForm] = useState(empty)
    const [baseline, setBaseline] = useState(empty)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const dirty = !sameRole(form, baseline)

    useEffect(() => {
        getRolesMeta().then((m) => {
            setModules(m.modules); setCaps(m.capabilities); setParents(m.parents ?? {}); setModuleCaps(m.moduleCapabilities ?? {})
        }).catch(() => {})
    }, [])

    useEffect(() => {
        if (!id) return
        getRole(id).then((r) => {
            const loaded = { key: r.key, label: r.label, capabilities: (r.capabilities ?? {}) as CapMap }
            setForm(loaded); setBaseline(loaded)
        }).catch((e) => setError(e?.response?.data?.error ?? e?.message ?? T.loadError))
    }, [id])

    const has = (m: string, c: Capability) => form.capabilities[m]?.includes(c) ?? false

    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError('')
        try {
            if (id) {
                await updateRole(id, { label: form.label, capabilities: form.capabilities })
                toast(T.saved)
            } else {
                await createRole({ key: form.key, label: form.label, capabilities: form.capabilities })
                toast(T.created); closeTab('role-new')
            }
            publishChange('roles')
            setBaseline(form)
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(msg)
            return { ok: false, error: msg }
        } finally { setBusy(false) }
    }

    function submit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !readOnly, save)

    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page">
            <div className="form-top">
                <div className="form-header">
                    <h1>{readOnly ? T.titleReadOnly : id ? T.titleEdit : T.titleNew} <InfoTip>{T.info}</InfoTip></h1>
                    {!readOnly && (
                        <button type="submit" disabled={busy || !dirty}>
                            {busy ? '…' : id ? T.apply : T.create}
                        </button>
                    )}
                </div>
                {readOnly && <div className="notice">{T.readOnlyNotice}</div>}
                {error && <div className="notice err">{error}</div>}
            </div>

            <fieldset disabled={busy || readOnly} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.label}</label>
                            <input type="text" value={form.label} required
                                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} /></div>
                        {!id && (
                            <div className="field"><label>{T.key}<span className="hint">{T.keyHint}</span></label>
                                <input type="text" value={form.key} required placeholder={T.keyPlaceholder}
                                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))} /></div>
                        )}
                    </div>

                    <h2 style={{ marginBottom: 8 }}>{T.rightsPerModule}</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>{T.module}</th>
                                {caps.map((c) => <th key={c} className="center">{CAP_LABELS[c] ?? c}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {modules.map((m) => (
                                <tr key={m}>
                                    <td style={parents[m] ? { paddingLeft: 28 } : undefined}>
                                        {parents[m] && <span className="muted" style={{ marginRight: 4 }}>↳</span>}
                                        {textOf(MODULE_LABELS, m)}
                                    </td>
                                    {caps.map((c) => {
                                        const allowed = (moduleCaps[m] ?? caps).includes(c)
                                        return (
                                            <td key={c} className="center">
                                                {allowed ? (
                                                    <input type="checkbox" checked={has(m, c)}
                                                        onChange={() => setForm((f) => ({ ...f, capabilities: toggle(f.capabilities, m, c) }))} />
                                                ) : (
                                                    <span className="muted" title={T.notApplicable}>—</span>
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </fieldset>
        </form>
    )
}
